import type { Point, Wall, Room } from '../types';
import { isNearPoint, pointToLineDistance, getRoomBounds } from './floorPlanUtils';

// Wall snapping configuration based on professional CAD standards
export interface WallSnapConfig {
  // Snap distances (in pixels)
  magneticSnapDistance: number;    // Distance for magnetic attraction to walls
  endpointSnapDistance: number;    // Distance for endpoint snapping
  parallelSnapDistance: number;    // Distance for parallel wall detection
  perpendicularSnapDistance: number; // Distance for perpendicular alignment
  
  // Angular tolerances (in degrees)
  parallelAngleTolerance: number;  // Angle tolerance for parallel detection
  perpendicularAngleTolerance: number; // Angle tolerance for perpendicular detection
  
  // Visual feedback
  showSnapPreview: boolean;        // Show visual snap indicators
  snapFeedbackColor: string;       // Color for snap feedback lines
  
  // Connection preferences
  preferWallExtensions: boolean;   // Prefer extending existing walls
  autoConnectThreshold: number;    // Distance threshold for auto-connection
  connectionDistanceThreshold: number; // Distance threshold for room connection
}

// Default configuration with conservative 3cm snap distances
export const DEFAULT_SNAP_CONFIG: WallSnapConfig = {
  magneticSnapDistance: 3,         // 3cm magnetic attraction (3 pixels)
  endpointSnapDistance: 3,         // 3cm endpoint snapping (3 pixels)
  parallelSnapDistance: 3,         // 3cm parallel wall detection (3 pixels)
  perpendicularSnapDistance: 3,    // 3cm perpendicular alignment (3 pixels)
  parallelAngleTolerance: 5,       // 5 degrees tolerance
  perpendicularAngleTolerance: 5,  // 5 degrees tolerance
  showSnapPreview: true,
  snapFeedbackColor: '#00FF00',    // Green like AutoCAD
  preferWallExtensions: true,
  autoConnectThreshold: 3,         // 3cm auto-connection (3 pixels)
  connectionDistanceThreshold: 3,  // 3cm room connection (3 pixels)
};

// Snap point types (similar to AutoCAD's osnap modes)
export type SnapType = 
  | 'endpoint'           // Wall endpoints
  | 'midpoint'          // Wall midpoints
  | 'perpendicular'     // Perpendicular to wall
  | 'parallel'          // Parallel to wall
  | 'extension'         // Wall extension
  | 'intersection'      // Wall intersections
  | 'tangent'           // Tangent points
  | 'center'            // Room centers
  | 'nearest'           // Nearest point on wall
  | 'none';

export interface SnapResult {
  snappedPoint: Point;
  snapType: SnapType;
  snapTarget?: Wall | Room;
  snapAngle?: number;
  snapDistance: number;
  previewLine?: { start: Point; end: Point };
  confidence: number; // 0-1, how confident the snap is
}

export interface WallSnapContext {
  existingWalls: Wall[];
  existingRooms: Room[];
  currentRoom?: Room;
  dragStartPoint: Point;
  currentMousePoint: Point;
  scale: number;
  config: WallSnapConfig;
}

/**
 * Professional wall snapping system inspired by AutoCAD and Revit
 * Implements multiple snap modes with priority-based selection
 */
export class WallSnappingSystem {
  private config: WallSnapConfig;

  constructor(config: WallSnapConfig = DEFAULT_SNAP_CONFIG) {
    this.config = config;
  }

  /**
   * Main snapping function - finds the best snap point based on professional CAD logic
   */
  public findBestSnapPoint(context: WallSnapContext): SnapResult {
    const snapCandidates: SnapResult[] = [];
    
    // 🔍 DEBUG: Log snap detection process
    console.log('🔍 Starting snap detection:', {
      existingWalls: context.existingWalls.length,
      existingRooms: context.existingRooms.length,
      mousePoint: context.currentMousePoint,
      magneticDistance: context.config.magneticSnapDistance
    });
    
    // 1. Endpoint snapping (highest priority - like AutoCAD)
    const endpointSnaps = this.findEndpointSnaps(context);
    snapCandidates.push(...endpointSnaps);
    console.log(`📍 Endpoint snaps found: ${endpointSnaps.length}`);
    
    // 2. Wall extension snapping (high priority)
    const extensionSnaps = this.findExtensionSnaps(context);
    snapCandidates.push(...extensionSnaps);
    console.log(`📏 Extension snaps found: ${extensionSnaps.length}`);
    
    // 3. Perpendicular snapping (medium-high priority)
    const perpendicularSnaps = this.findPerpendicularSnaps(context);
    snapCandidates.push(...perpendicularSnaps);
    console.log(`⟂ Perpendicular snaps found: ${perpendicularSnaps.length}`);
    
    // 4. Parallel snapping (medium priority)
    const parallelSnaps = this.findParallelSnaps(context);
    snapCandidates.push(...parallelSnaps);
    console.log(`∥ Parallel snaps found: ${parallelSnaps.length}`);
    
    // 5. Midpoint snapping (medium priority)
    const midpointSnaps = this.findMidpointSnaps(context);
    snapCandidates.push(...midpointSnaps);
    console.log(`⌐ Midpoint snaps found: ${midpointSnaps.length}`);
    
    // 6. Intersection snapping (medium priority)
    const intersectionSnaps = this.findIntersectionSnaps(context);
    snapCandidates.push(...intersectionSnaps);
    console.log(`✗ Intersection snaps found: ${intersectionSnaps.length}`);
    
    // 7. Nearest point snapping (lowest priority)
    const nearestSnaps = this.findNearestPointSnaps(context);
    snapCandidates.push(...nearestSnaps);
    console.log(`📌 Nearest snaps found: ${nearestSnaps.length}`);
    
    console.log(`🎯 Total snap candidates: ${snapCandidates.length}`);
    
    // Select best snap based on professional CAD prioritization
    const bestSnap = this.selectBestSnap(snapCandidates, context);
    console.log('🏆 Best snap selected:', bestSnap.snapType, bestSnap.snapDistance);
    
    return bestSnap;
  }

  /**
   * Enhanced room dragging with professional wall snapping
   */
  public snapRoomDuringDrag(
    room: Room,
    dragStartPoint: Point,
    currentMousePoint: Point,
    existingWalls: Wall[],
    existingRooms: Room[],
    scale: number = 1
  ): SnapResult {
    const context: WallSnapContext = {
      existingWalls,
      existingRooms,
      currentRoom: room,
      dragStartPoint,
      currentMousePoint,
      scale,
      config: this.config
    };
    
    // Get room bounds for snapping calculations from room walls
    const roomBounds = room.walls && room.walls.length > 0 
      ? this.calculateRoomBoundsFromWalls(room.walls)
      : { minX: -50, maxX: 50, minY: -50, maxY: 50 }; // Default 100x100 room
    
    const dragOffset = {
      x: currentMousePoint.x - dragStartPoint.x,
      y: currentMousePoint.y - dragStartPoint.y
    };
    
    return this.snapRoomToWalls(roomBounds, dragOffset, context);
  }

  /**
   * Room-specific snapping for connecting rooms to existing walls
   */
  private snapRoomToWalls(
    roomBounds: { minX: number; maxX: number; minY: number; maxY: number },
    dragOffset: Point,
    context: WallSnapContext
  ): SnapResult {
    const { existingWalls, config } = context;
    const snapCandidates: SnapResult[] = [];
    
    // Get room corner points
    const roomCorners = [
      { x: roomBounds.minX + dragOffset.x, y: roomBounds.minY + dragOffset.y },
      { x: roomBounds.maxX + dragOffset.x, y: roomBounds.minY + dragOffset.y },
      { x: roomBounds.maxX + dragOffset.x, y: roomBounds.maxY + dragOffset.y },
      { x: roomBounds.minX + dragOffset.x, y: roomBounds.maxY + dragOffset.y }
    ];
    
    // Check each corner for wall snapping
    for (const corner of roomCorners) {
      const cornerContext = { ...context, currentMousePoint: corner };
      const cornerSnap = this.findBestSnapPoint(cornerContext);
      
      if (cornerSnap.snapType !== 'none') {
        // Calculate room offset needed to achieve this snap
        const snapOffset = {
          x: cornerSnap.snappedPoint.x - corner.x,
          y: cornerSnap.snappedPoint.y - corner.y
        };
        
        snapCandidates.push({
          ...cornerSnap,
          snappedPoint: {
            x: dragOffset.x + snapOffset.x,
            y: dragOffset.y + snapOffset.y
          },
          confidence: cornerSnap.confidence * 0.9 // Slightly lower confidence for room snapping
        });
      }
    }
    
    // Check room edges for wall alignment
    const roomEdges = this.getRoomEdges(roomBounds, dragOffset);
    for (const edge of roomEdges) {
      for (const wall of existingWalls) {
        const alignment = this.checkWallAlignment(edge, wall, config);
        if (alignment) {
          snapCandidates.push(alignment);
        }
      }
    }
    
    return this.selectBestSnap(snapCandidates, context);
  }

  // Snap detection methods

  /**
   * Endpoint snapping - snap to wall start/end points (AutoCAD ENDpoint)
   */
  private findEndpointSnaps(context: WallSnapContext): SnapResult[] {
    const results: SnapResult[] = [];
    const { existingWalls, currentMousePoint, config } = context;
    
    for (const wall of existingWalls) {
      // Check start point
      const startDistance = this.getDistance(currentMousePoint, wall.start);
      if (startDistance <= config.endpointSnapDistance) {
        results.push({
          snappedPoint: { ...wall.start },
          snapType: 'endpoint',
          snapTarget: wall,
          snapDistance: startDistance,
          confidence: 1.0 - (startDistance / config.endpointSnapDistance)
        });
      }
      
      // Check end point
      const endDistance = this.getDistance(currentMousePoint, wall.end);
      if (endDistance <= config.endpointSnapDistance) {
        results.push({
          snappedPoint: { ...wall.end },
          snapType: 'endpoint',
          snapTarget: wall,
          snapDistance: endDistance,
          confidence: 1.0 - (endDistance / config.endpointSnapDistance)
        });
      }
    }
    
    return results;
  }

  /**
   * Wall extension snapping - extend existing walls (AutoCAD EXTension)
   */
  private findExtensionSnaps(context: WallSnapContext): SnapResult[] {
    const results: SnapResult[] = [];
    const { existingWalls, currentMousePoint, config } = context;
    
    for (const wall of existingWalls) {
      const wallVector = {
        x: wall.end.x - wall.start.x,
        y: wall.end.y - wall.start.y
      };
      const wallLength = Math.sqrt(wallVector.x ** 2 + wallVector.y ** 2);
      
      if (wallLength === 0) continue;
      
      const wallDirection = {
        x: wallVector.x / wallLength,
        y: wallVector.y / wallLength
      };
      
      // Check extension from end point (forward)
      const toMouseFromEnd = {
        x: currentMousePoint.x - wall.end.x,
        y: currentMousePoint.y - wall.end.y
      };
      const projectionFromEnd = toMouseFromEnd.x * wallDirection.x + toMouseFromEnd.y * wallDirection.y;
      
      if (projectionFromEnd > 0) { // Point is beyond the end
        const extensionPoint = {
          x: wall.end.x + projectionFromEnd * wallDirection.x,
          y: wall.end.y + projectionFromEnd * wallDirection.y
        };
        const distanceToExtension = this.getDistance(currentMousePoint, extensionPoint);
        
        if (distanceToExtension <= config.magneticSnapDistance) {
          results.push({
            snappedPoint: extensionPoint,
            snapType: 'extension',
            snapTarget: wall,
            snapDistance: distanceToExtension,
            previewLine: { start: wall.end, end: extensionPoint },
            confidence: 0.8 - (distanceToExtension / config.magneticSnapDistance)
          });
        }
      }
    }
    
    return results;
  }

  /**
   * Perpendicular snapping - snap perpendicular to walls (AutoCAD PERpendicular)
   */
  private findPerpendicularSnaps(context: WallSnapContext): SnapResult[] {
    const results: SnapResult[] = [];
    const { existingWalls, currentMousePoint, config } = context;
    
    for (const wall of existingWalls) {
      const wallVector = {
        x: wall.end.x - wall.start.x,
        y: wall.end.y - wall.start.y
      };
      const wallLength = Math.sqrt(wallVector.x ** 2 + wallVector.y ** 2);
      
      if (wallLength === 0) continue;
      
      // Find perpendicular point on wall
      const pointVector = {
        x: currentMousePoint.x - wall.start.x,
        y: currentMousePoint.y - wall.start.y
      };
      const projection = (pointVector.x * wallVector.x + pointVector.y * wallVector.y) / (wallLength ** 2);
      
      // Only consider if projection is within the wall segment
      if (projection >= 0 && projection <= 1) {
        const perpendicularPoint = {
          x: wall.start.x + projection * wallVector.x,
          y: wall.start.y + projection * wallVector.y
        };
        
        const distanceToPerpendicular = this.getDistance(currentMousePoint, perpendicularPoint);
        
        if (distanceToPerpendicular <= config.perpendicularSnapDistance) {
          results.push({
            snappedPoint: perpendicularPoint,
            snapType: 'perpendicular',
            snapTarget: wall,
            snapDistance: distanceToPerpendicular,
            confidence: 0.7 - (distanceToPerpendicular / config.perpendicularSnapDistance) * 0.3
          });
        }
      }
    }
    
    return results;
  }

  /**
   * Parallel snapping - snap parallel to existing walls
   */
  private findParallelSnaps(context: WallSnapContext): SnapResult[] {
    const results: SnapResult[] = [];
    const { existingWalls, currentMousePoint, dragStartPoint, config } = context;
    
    const dragVector = {
      x: currentMousePoint.x - dragStartPoint.x,
      y: currentMousePoint.y - dragStartPoint.y
    };
    const dragLength = Math.sqrt(dragVector.x ** 2 + dragVector.y ** 2);
    
    if (dragLength === 0) return results;
    
    for (const wall of existingWalls) {
      const wallVector = {
        x: wall.end.x - wall.start.x,
        y: wall.end.y - wall.start.y
      };
      const wallLength = Math.sqrt(wallVector.x ** 2 + wallVector.y ** 2);
      
      if (wallLength === 0) continue;
      
      const angle = this.getAngleBetweenVectors(dragVector, wallVector);
      const parallelAngle = Math.min(Math.abs(angle), Math.abs(180 - Math.abs(angle)));
      
      if (parallelAngle <= config.parallelAngleTolerance) {
        // Create parallel snap point
        const wallDirection = {
          x: wallVector.x / wallLength,
          y: wallVector.y / wallLength
        };
        
        const parallelPoint = {
          x: dragStartPoint.x + dragLength * wallDirection.x,
          y: dragStartPoint.y + dragLength * wallDirection.y
        };
        
        const distanceToParallel = this.getDistance(currentMousePoint, parallelPoint);
        
        if (distanceToParallel <= config.parallelSnapDistance) {
          results.push({
            snappedPoint: parallelPoint,
            snapType: 'parallel',
            snapTarget: wall,
            snapDistance: distanceToParallel,
            snapAngle: this.getVectorAngle(wallDirection),
            previewLine: { start: dragStartPoint, end: parallelPoint },
            confidence: 0.6 - (parallelAngle / config.parallelAngleTolerance) * 0.2
          });
        }
      }
    }
    
    return results;
  }

  /**
   * Midpoint snapping - snap to wall midpoints (AutoCAD MIDpoint)
   */
  private findMidpointSnaps(context: WallSnapContext): SnapResult[] {
    const results: SnapResult[] = [];
    const { existingWalls, currentMousePoint, config } = context;
    
    for (const wall of existingWalls) {
      const midpoint = {
        x: (wall.start.x + wall.end.x) / 2,
        y: (wall.start.y + wall.end.y) / 2
      };
      
      const distanceToMidpoint = this.getDistance(currentMousePoint, midpoint);
      
      if (distanceToMidpoint <= config.endpointSnapDistance) {
        results.push({
          snappedPoint: midpoint,
          snapType: 'midpoint',
          snapTarget: wall,
          snapDistance: distanceToMidpoint,
          confidence: 0.5 - (distanceToMidpoint / config.endpointSnapDistance) * 0.2
        });
      }
    }
    
    return results;
  }

  /**
   * Intersection snapping - snap to wall intersections (AutoCAD INTersection)
   */
  private findIntersectionSnaps(context: WallSnapContext): SnapResult[] {
    const results: SnapResult[] = [];
    const { existingWalls, currentMousePoint, config } = context;
    
    // Find intersections between all wall pairs
    for (let i = 0; i < existingWalls.length; i++) {
      for (let j = i + 1; j < existingWalls.length; j++) {
        const wall1 = existingWalls[i];
        const wall2 = existingWalls[j];
        
        const intersection = this.getLineIntersection(wall1.start, wall1.end, wall2.start, wall2.end);
        
        if (intersection) {
          const distanceToIntersection = this.getDistance(currentMousePoint, intersection);
          
          if (distanceToIntersection <= config.endpointSnapDistance) {
            results.push({
              snappedPoint: intersection,
              snapType: 'intersection',
              snapDistance: distanceToIntersection,
              confidence: 0.6 - (distanceToIntersection / config.endpointSnapDistance) * 0.2
            });
          }
        }
      }
    }
    
    return results;
  }

  /**
   * Nearest point snapping - snap to nearest point on walls (AutoCAD NEArest)
   */
  private findNearestPointSnaps(context: WallSnapContext): SnapResult[] {
    const results: SnapResult[] = [];
    const { existingWalls, currentMousePoint, config } = context;
    
    for (const wall of existingWalls) {
      const nearestPoint = this.getNearestPointOnLine(currentMousePoint, wall.start, wall.end);
      const distanceToNearest = this.getDistance(currentMousePoint, nearestPoint);
      
      if (distanceToNearest <= config.magneticSnapDistance) {
        // Check if point is actually on the line segment
        const isOnSegment = this.isPointOnLineSegment(nearestPoint, wall.start, wall.end);
        
        if (isOnSegment) {
          results.push({
            snappedPoint: nearestPoint,
            snapType: 'nearest',
            snapTarget: wall,
            snapDistance: distanceToNearest,
            confidence: 0.3 - (distanceToNearest / config.magneticSnapDistance) * 0.2
          });
        }
      }
    }
    
    return results;
  }

  /**
   * Select the best snap from candidates based on professional CAD prioritization
   */
  private selectBestSnap(candidates: SnapResult[], context: WallSnapContext): SnapResult {
    console.log(`🎯 Selecting best snap from ${candidates.length} candidates`);
    
    if (candidates.length === 0) {
      console.log('❌ No snap candidates found, returning none');
      return {
        snappedPoint: { ...context.currentMousePoint },
        snapType: 'none',
        snapDistance: 0,
        confidence: 0
      };
    }
    
    // 🔍 DEBUG: Log all candidates before sorting
    candidates.forEach((candidate, i) => {
      console.log(`📋 Candidate ${i}:`, {
        type: candidate.snapType,
        distance: candidate.snapDistance.toFixed(1),
        confidence: candidate.confidence.toFixed(2),
        point: `(${candidate.snappedPoint.x.toFixed(1)}, ${candidate.snappedPoint.y.toFixed(1)})`
      });
    });
    
    // Sort by priority (snap type) and then by confidence/distance
    const priorityOrder: SnapType[] = [
      'endpoint', 'extension', 'perpendicular', 'parallel', 
      'midpoint', 'intersection', 'nearest'
    ];
    
    candidates.sort((a, b) => {
      const aPriority = priorityOrder.indexOf(a.snapType);
      const bPriority = priorityOrder.indexOf(b.snapType);
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority; // Lower index = higher priority
      }
      
      // Same priority - sort by confidence (higher is better)
      if (Math.abs(a.confidence - b.confidence) > 0.01) {
        return b.confidence - a.confidence;
      }
      
      // Same confidence - sort by distance (closer is better)
      return a.snapDistance - b.snapDistance;
    });
    
    const bestCandidate = candidates[0];
    console.log(`🏆 Selected best snap:`, {
      type: bestCandidate.snapType,
      distance: bestCandidate.snapDistance.toFixed(1),
      confidence: bestCandidate.confidence.toFixed(2),
      point: `(${bestCandidate.snappedPoint.x.toFixed(1)}, ${bestCandidate.snappedPoint.y.toFixed(1)})`
    });
    
    return bestCandidate;
  }

  // Utility methods
  private getDistance(p1: Point, p2: Point): number {
    return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
  }

  private getAngleBetweenVectors(v1: Point, v2: Point): number {
    const dot = v1.x * v2.x + v1.y * v2.y;
    const det = v1.x * v2.y - v1.y * v2.x;
    const angle = Math.atan2(det, dot) * (180 / Math.PI);
    return angle;
  }

  private getVectorAngle(vector: Point): number {
    return Math.atan2(vector.y, vector.x) * (180 / Math.PI);
  }

  private getLineIntersection(p1: Point, p2: Point, p3: Point, p4: Point): Point | null {
    const denom = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
    if (Math.abs(denom) < 1e-10) return null;
    
    const t = ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x)) / denom;
    const u = -((p1.x - p2.x) * (p1.y - p3.y) - (p1.y - p2.y) * (p1.x - p3.x)) / denom;
    
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return {
        x: p1.x + t * (p2.x - p1.x),
        y: p1.y + t * (p2.y - p1.y)
      };
    }
    
    return null;
  }

  private getNearestPointOnLine(point: Point, lineStart: Point, lineEnd: Point): Point {
    const lineVector = { x: lineEnd.x - lineStart.x, y: lineEnd.y - lineStart.y };
    const lineLength = Math.sqrt(lineVector.x ** 2 + lineVector.y ** 2);
    
    if (lineLength === 0) return { ...lineStart };
    
    const pointVector = { x: point.x - lineStart.x, y: point.y - lineStart.y };
    const projection = (pointVector.x * lineVector.x + pointVector.y * lineVector.y) / (lineLength ** 2);
    const clampedProjection = Math.max(0, Math.min(1, projection));
    
    return {
      x: lineStart.x + clampedProjection * lineVector.x,
      y: lineStart.y + clampedProjection * lineVector.y
    };
  }

  private isPointOnLineSegment(point: Point, lineStart: Point, lineEnd: Point, tolerance: number = 1): boolean {
    const distanceToLine = pointToLineDistance(point, lineStart, lineEnd);
    if (distanceToLine > tolerance) return false;
    
    const lineLength = this.getDistance(lineStart, lineEnd);
    const distanceToStart = this.getDistance(point, lineStart);
    const distanceToEnd = this.getDistance(point, lineEnd);
    
    return Math.abs(distanceToStart + distanceToEnd - lineLength) < tolerance;
  }

  private calculateRoomBounds(points: Point[]): { minX: number; maxX: number; minY: number; maxY: number } {
    if (points.length === 0) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }
    
    let minX = points[0].x;
    let maxX = points[0].x;
    let minY = points[0].y;
    let maxY = points[0].y;
    
    for (const point of points) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }
    
    return { minX, maxX, minY, maxY };
  }

  private calculateRoomBoundsFromWalls(walls: Wall[]): { minX: number; maxX: number; minY: number; maxY: number } {
    if (walls.length === 0) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }
    
    // Get all points from walls
    const points: Point[] = [];
    for (const wall of walls) {
      points.push(wall.start, wall.end);
    }
    
    return this.calculateRoomBounds(points);
  }

  private getRoomEdges(bounds: { minX: number; maxX: number; minY: number; maxY: number }, offset: Point) {
    return [
      { start: { x: bounds.minX + offset.x, y: bounds.minY + offset.y }, end: { x: bounds.maxX + offset.x, y: bounds.minY + offset.y } }, // Top
      { start: { x: bounds.maxX + offset.x, y: bounds.minY + offset.y }, end: { x: bounds.maxX + offset.x, y: bounds.maxY + offset.y } }, // Right
      { start: { x: bounds.maxX + offset.x, y: bounds.maxY + offset.y }, end: { x: bounds.minX + offset.x, y: bounds.maxY + offset.y } }, // Bottom
      { start: { x: bounds.minX + offset.x, y: bounds.maxY + offset.y }, end: { x: bounds.minX + offset.x, y: bounds.minY + offset.y } }  // Left
    ];
  }

  private checkWallAlignment(roomEdge: { start: Point; end: Point }, wall: Wall, config: WallSnapConfig): SnapResult | null {
    const roomVector = { x: roomEdge.end.x - roomEdge.start.x, y: roomEdge.end.y - roomEdge.start.y };
    const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
    
    const angle = this.getAngleBetweenVectors(roomVector, wallVector);
    const parallelAngle = Math.min(Math.abs(angle), Math.abs(180 - Math.abs(angle)));
    
    if (parallelAngle <= config.parallelAngleTolerance) {
      // Check if edges are close enough to snap
      const distance = pointToLineDistance(roomEdge.start, wall.start, wall.end);
      
      if (distance <= config.magneticSnapDistance) {
        const nearestPoint = this.getNearestPointOnLine(roomEdge.start, wall.start, wall.end);
        return {
          snappedPoint: nearestPoint,
          snapType: 'parallel',
          snapTarget: wall,
          snapDistance: distance,
          confidence: 0.7 - (distance / config.magneticSnapDistance) * 0.3
        };
      }
    }
    
    return null;
  }
}

/**
 * Convenience function to create a wall snapping system with room dragging
 */
export function createRoomWallSnappingSystem(config?: Partial<WallSnapConfig>): WallSnappingSystem {
  const fullConfig = { ...DEFAULT_SNAP_CONFIG, ...config };
  return new WallSnappingSystem(fullConfig);
}

/**
 * Enhanced room dragging with professional wall snapping
 */
export function snapRoomDuringDrag(
  room: Room,
  dragStartPoint: Point,
  currentMousePoint: Point,
  existingWalls: Wall[],
  existingRooms: Room[],
  scale: number = 1,
  config?: Partial<WallSnapConfig>
): SnapResult {
  // Debug logging disabled during dragging preview for performance
  // console.log('🧲 snapRoomDuringDrag called with:', {
  //   roomWallsCount: room.walls?.length || 0,
  //   dragStart: dragStartPoint,
  //   currentMouse: currentMousePoint,
  //   existingWallsCount: existingWalls.length,
  //   existingRoomsCount: existingRooms.length,
  //   scale: scale,
  //   configProvided: !!config
  // });
  
  const snappingSystem = createRoomWallSnappingSystem(config);
  
  const result = snappingSystem.snapRoomDuringDrag(
    room,
    dragStartPoint,
    currentMousePoint,
    existingWalls,
    existingRooms,
    scale
  );
  
  // Debug logging disabled during dragging preview for performance
  // console.log('🧲 snapRoomDuringDrag result:', {
  //   snapType: result.snapType,
  //   snapDistance: result.snapDistance,
  //   confidence: result.confidence,
  //   hasPreviewLine: !!result.previewLine
  // });
  
  return result;
} 